import React, { JSX, useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Box, CircularProgress, MenuItem, Select, SelectChangeEvent, Stack, TableCell, TableRow } from '@mui/material';

import { Badge, Tag, Text } from '~/components/Experiences/components';

import {
  EXP_PROVIDER_LED,
  curationCommentOptions,
  getStatusByCurated,
  statusOptions,
  themes,
} from '~/consts/experiences';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import { CategoryItem, CurationStatus, ExperienceOffer, curationExperience } from '~/services/ExperiencesService';
import { formatDateShort } from '~/services/TimeService';

import { currencyFormatter } from '../../helpers';

// Temporary type extension until contract is published with curationComment field
type ExperienceOfferWithCurationComment = ExperienceOffer & {
  curationComment?: string;
};

type RowProps = {
  onClick: () => void;
  experience: ExperienceOfferWithCurationComment;
};

export function ExperienceRowContent({ experience, onClick }: RowProps): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const [curationStatus, setCurationStatus] = useState<CurationStatus>(experience.curationStatus);
  const [curationComment, setCurationComment] = useState<string>(experience.curationComment || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [isCommentSelectOpen, setIsCommentSelectOpen] = useState(false);
  const [isUpdatingComment, setIsUpdatingComment] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const { tenant } = useCurrentTenant();

  const status = getStatusByCurated(curationStatus);

  const salesPrice = experience?.pricing?.salesPrice;

  const leadImage = experience.images?.[0];
  const leadImageUrl = leadImage?.urls?.[0]?.url;
  const leadImageAlt = leadImage?.description || `${experience.title} image`;

  const hasPriceOverridden = !!experience?.margin || experience?.tickets?.some((ticket) => ticket.margin);

  const retailPriceFormatted = () => {
    return currencyFormatter(
      experience?.pricing?.providerBaseSalesPriceCurrency,
      experience?.pricing?.providerBaseSalesPrice,
    );
  };

  const salesPriceFormatted = () => {
    return currencyFormatter(experience?.pricing?.currency, salesPrice);
  };

  const handleBadgeClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setIsEditing(true);
    setIsSelectOpen(true);
  }, []);

  const handleSelectChange = useCallback(
    async (event: SelectChangeEvent<CurationStatus>) => {
      const newStatus = event.target.value as CurationStatus;

      setIsEditing(false);
      setIsSelectOpen(false);

      if (newStatus === curationStatus) {
        return;
      }

      setIsUpdating(true);

      try {
        await curationExperience(experience.id, tenant.brand, {
          curationStatus: newStatus,
        });
        setCurationStatus(newStatus);
        enqueueSnackbar(`Curation status updated to ${newStatus}`, { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(`Failed to update curation status: ${error.message}`, { variant: 'error' });
      } finally {
        setIsUpdating(false);
      }
    },
    [curationStatus, experience.id, tenant, enqueueSnackbar],
  );

  const handleSelectClose = useCallback(() => {
    setIsSelectOpen(false);
    setIsEditing(false);
  }, []);

  const handleCommentClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setIsEditingComment(true);
    setIsCommentSelectOpen(true);
  }, []);

  const handleCommentSelectChange = useCallback(
    async (event: SelectChangeEvent<string>) => {
      const newComment = event.target.value;

      setIsEditingComment(false);
      setIsCommentSelectOpen(false);

      if (newComment === curationComment) {
        return;
      }

      setIsUpdatingComment(true);

      try {
        await curationExperience(experience.id, tenant.brand, {
          curationComment: newComment,
        });
        setCurationComment(newComment);
        enqueueSnackbar(newComment ? `Curation comment updated to "${newComment}"` : 'Curation comment cleared', {
          variant: 'success',
        });
      } catch (error) {
        enqueueSnackbar(`Failed to update curation comment: ${error.message}`, { variant: 'error' });
      } finally {
        setIsUpdatingComment(false);
      }
    },
    [curationComment, experience.id, tenant, enqueueSnackbar],
  );

  const handleCommentSelectClose = useCallback(() => {
    setIsCommentSelectOpen(false);
    setIsEditingComment(false);
  }, []);

  return (
    <TableRow>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {!isEditing ? (
            <Box onClick={handleBadgeClick} sx={{ cursor: 'pointer' }}>
              <Badge title={status.title} color={status.color} background="transparent" borderColor={status.color} />
            </Box>
          ) : (
            <Select
              value={curationStatus}
              onChange={handleSelectChange}
              onClose={handleSelectClose}
              open={isSelectOpen}
              size="small"
              disabled={isUpdating}
              autoFocus
              sx={{ minWidth: 120, fontSize: '0.875rem' }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          )}
          {isUpdating && <CircularProgress size={16} />}
        </Stack>
      </TableCell>

      <TableCell onClick={onClick}>
        {leadImageUrl ? (
          <Box
            component="img"
            src={leadImageUrl}
            alt={leadImageAlt}
            sx={{ width: 240, height: 135, objectFit: 'cover', borderRadius: 1 }}
          />
        ) : (
          <Text weight={300} size="14px">
            <i>No image</i>
          </Text>
        )}
      </TableCell>

      <TableCell>
        <div>
          <Stack direction="row" alignItems="self-start">
            <Text weight={600} title={experience.title}>
              {experience.title}
            </Text>
            {experience?.leExclusive && <Tag name="LE Exclusive" background={themes.secondaryGreen} />}
            {experience?.isFutureOffer && <Tag name="Scheduled" background={themes.secondaryGrey} />}
          </Stack>
          <Text weight={300} title={experience?.shortDescription ?? experience?.description}>
            {experience?.shortDescription ?? experience?.description ?? <i>no description</i>}
          </Text>
          <Text weight={300} title={experience.title} mt="10px">
            <Box className="experience-id-badge" component="span" title={`Experience ID: ${experience.id}`}>
              ID: <code>{experience.id}</code>
            </Box>
          </Text>
          {experience?.isFutureOffer && experience?.scheduledStartDate && (
            <Text weight={300} title={experience.title}>
              Start date: {formatDateShort(new Date(experience?.scheduledStartDate))}
            </Text>
          )}
          {experience?.isFutureOffer && experience?.scheduledEndDate && (
            <Text weight={300} title={experience.title}>
              End date: {formatDateShort(new Date(experience?.scheduledEndDate))}
            </Text>
          )}
        </div>
      </TableCell>

      <TableCell onClick={onClick}>{retailPriceFormatted()}</TableCell>

      <TableCell onClick={onClick}>{salesPriceFormatted()}</TableCell>

      <TableCell onClick={onClick}>{experience?.pricing?.margin?.toFixed(1) + '%'}</TableCell>

      <TableCell onClick={onClick}>{hasPriceOverridden ? 'YES' : 'NO'}</TableCell>

      <TableCell onClick={onClick}>
        {experience?.brands?.map((brand, i: number) => (
          <Tag outline name={brand} key={`${i}-${brand}`} />
        ))}
      </TableCell>

      <TableCell onClick={onClick}>
        <Stack spacing={0.5} direction="column" alignItems="flex-start">
          <Text weight={300} size="15px">
            {typeof experience.reviewsRating === 'number' && experience.reviewsTotal > 0
              ? `${experience.reviewsRating.toFixed(1)} ★`
              : ''}
          </Text>
          <Text weight={100} size="9px" color="grey.600">
            {experience.reviewsTotal
              ? `${experience.reviewsTotal} review${experience.reviewsTotal > 1 ? 's' : ''}`
              : 'No reviews'}
          </Text>
          {experience.reviewsSource && (
            <Text weight={100} size="9px" color="grey.600">
              Source: {experience.reviewsSource}
            </Text>
          )}
        </Stack>
      </TableCell>

      <TableCell onClick={onClick}>
        {experience.categories.map((category: CategoryItem, i: number) => (
          <Tag outline name={category.label} key={`${i}-${category.label}`} />
        ))}
      </TableCell>

      <TableCell onClick={(e) => e.stopPropagation()}>
        {experience.provider === EXP_PROVIDER_LED ? (
          <Box onClick={onClick}>
            <Text weight={300} size="14px">
              -
            </Text>
          </Box>
        ) : (
          <Stack direction="row" alignItems="center" spacing={1}>
            {!isEditingComment ? (
              <Box onClick={handleCommentClick} sx={{ cursor: 'pointer', minWidth: 120 }}>
                <Text weight={300} size="14px">
                  {curationComment || <i>Click to add</i>}
                </Text>
              </Box>
            ) : (
              <Select
                value={curationComment}
                onChange={handleCommentSelectChange}
                onClose={handleCommentSelectClose}
                open={isCommentSelectOpen}
                size="small"
                disabled={isUpdatingComment}
                autoFocus
                sx={{ minWidth: 180, fontSize: '0.875rem' }}
              >
                {curationCommentOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            )}
            {isUpdatingComment && <CircularProgress size={16} />}
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
}
